import {
  type CrossClientQueryAiEvaluationMode,
  type CrossClientQueryClauseResolverMapping,
  type CrossClientQueryClientQueryMode,
  type CrossClientQueryDataSource,
  type CrossClientQueryDecompositionResult,
  type CrossClientQueryLogicalQuestionDecomposition,
  type CrossClientQueryLogicalQuestionNNFDecomposition,
  type CrossClientQueryResultSummary,
} from '../../models/CrossClient'
import { type BaseRequest } from '../models'
import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'

const _TRANSCRIPTION_SERVICE_BASE_URL = import.meta.env.REACT_APP_AI_EXECUTE_API_BASE_URL!

interface CrossClientQueryDecomposeRequest extends BaseRequest {
  payload: {
    userQuestion: string
    sources?: CrossClientQueryDataSource[]
    clientQueryMode?: CrossClientQueryClientQueryMode
    decomposition?: CrossClientQueryLogicalQuestionDecomposition
    nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
    clauseResolverMapping?: CrossClientQueryClauseResolverMapping
  }
}

interface CrossClientQueryEvaluateRequest extends BaseRequest {
  executionId: string
  mode: CrossClientQueryAiEvaluationMode
  batchSize: number
}

interface CrossClientQueryEvaluateResolversRequest extends BaseRequest {
  executionId: string
}

interface CrossClientQueryEvaluateLocalResolversRequest extends BaseRequest {
  executionId: string
}

interface CrossClientQueryCancelRequest extends BaseRequest {
  executionId: string
}

export const crossClientExecutionApi = createApi({
  reducerPath: 'crossClientExecutionApi',
  baseQuery: baseQueryWithTokenRefresh(_TRANSCRIPTION_SERVICE_BASE_URL),
  endpoints: (builder) => ({
    decomposeCrossClientQuery: builder.mutation<CrossClientQueryDecompositionResult, CrossClientQueryDecomposeRequest>({
      query: ({ accountId, payload }) => ({
        url: `/accounts/${accountId}/cross-client-query/decompose`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: { data: CrossClientQueryDecompositionResult }) => response.data,
    }),
    evaluateClauseResolversCrossClientQuery: builder.mutation<CrossClientQueryResultSummary, CrossClientQueryEvaluateResolversRequest>({
      query: ({ accountId, executionId }) => ({
        url: `/accounts/${accountId}/cross-client-query/${executionId}/evaluate-resolvers`,
        method: 'POST',
      }),
      transformResponse: (response: { data: CrossClientQueryResultSummary }) => response.data,
    }),
    evaluateClauseLocalResolversCrossClientQuery: builder.mutation<void, CrossClientQueryEvaluateLocalResolversRequest>({
      query: ({ accountId, executionId }) => ({
        url: `/accounts/${accountId}/cross-client-query/${executionId}/evaluate-local-resolvers`,
        method: 'POST',
      }),
    }),
    evaluateCrossClientQuery: builder.mutation<void, CrossClientQueryEvaluateRequest>({
      query: ({ accountId, executionId, mode, batchSize }) => ({
        url: `/accounts/${accountId}/cross-client-query/${executionId}/evaluate`,
        method: 'POST',
        body: { mode, batchSize },
      }),
    }),
    cancelCrossClientQuery: builder.mutation<null, CrossClientQueryCancelRequest>({
      query: ({ accountId, executionId }) => ({
        url: `/accounts/${accountId}/cross-client-query/${executionId}/cancel`,
        method: 'POST',
      }),
    }),
  }),
})

export const {
  useDecomposeCrossClientQueryMutation,
  useEvaluateClauseResolversCrossClientQueryMutation,
  useEvaluateClauseLocalResolversCrossClientQueryMutation,
  useEvaluateCrossClientQueryMutation,
  useCancelCrossClientQueryMutation,
} = crossClientExecutionApi
